import React, { useCallback, useEffect, useRef, useState, } from 'react';
import classNames from 'classnames';
import { Button } from '../Button/Button';
import { Input } from '../Input/Input';
export const InlineEditable = ({ ariaLabel, className, defaultActive = false, active: controlledActive, defaultValue, editLabel = 'Edit value', errorMessage, inputProps, onActiveChange, onReset, onSubmit, resetLabel = 'Reset value', submitLabel = 'Submit value', submittedValue, valid, value, ...otherProps }) => {
    /**
     * Extract external onChange so it doesn't override internal handler.
     */
    const { onChange: externalOnChange, ...restOtherProps } = otherProps;
    /**
     * Get ref for input element.
     */
    const inputRef = useRef(null);
    /**
     * Determine if active state is controlled externally.
     */
    const isControlledActive = controlledActive !== undefined;
    /**
     * Manage active state to show editable mode.
     */
    const [internalActive, setInternalActive] = useState(defaultActive);
    const isActive = isControlledActive ? controlledActive : internalActive;
    const setIsActive = useCallback((val) => {
        if (!isControlledActive) {
            setInternalActive(val);
        }
        if (onActiveChange) {
            onActiveChange(val);
        }
    }, [isControlledActive, onActiveChange]);
    /**
     * Track internal baseline if not using submittedValue.
     */
    const [baselineValue, setBaselineValue] = useState(value ?? defaultValue ?? '');
    /**
     * Keep track of current value inside input.
     */
    const [currentValue, setCurrentValue] = useState(value ?? defaultValue ?? '');
    /**
     * Sync currentValue and initialValue with external value prop.
     */
    useEffect(() => {
        if (value !== undefined) {
            setCurrentValue(value);
            if (inputRef.current?.parentElement) {
                inputRef.current.parentElement.setAttribute('data-inline-editable-size', value.replaceAll(' ', '\u00a0'));
            }
        }
    }, [value, isActive]);
    /**
     *  When submittedValue changes, update baseline.
     *  This ensures that reset restores to the last saved value.
     */
    useEffect(() => {
        if (submittedValue !== undefined) {
            setBaselineValue(submittedValue);
            setCurrentValue(submittedValue);
            if (inputRef.current?.parentElement) {
                inputRef.current.parentElement.setAttribute('data-inline-editable-size', submittedValue.replaceAll(' ', '\u00a0'));
            }
        }
    }, [submittedValue]);
    /**
     * Set the {isActive} state to true and memoize the function.
     * @type {(function(): void)|*}
     */
    const activate = useCallback(() => {
        setIsActive(true);
        inputRef.current?.focus();
    }, [setIsActive]);
    /**
     * Set the {isActive} state to false and memoize the function.
     * @type {(function(): void)|*}
     */
    const deactivate = useCallback(() => {
        setIsActive(false);
        inputRef.current?.blur();
    }, [setIsActive]);
    /**
     * Handle key presses when inactive.
     * If user presses Enter → activate.
     * If user presses Tab → blur input.
     * If tries typing → block unless active.
     */
    const handleKeyDown = (event) => {
        if (!isActive) {
            if (event.key === 'Enter') {
                event.preventDefault();
                setIsActive(true);
                inputRef.current?.focus();
            }
            else if (event.key === 'Tab') {
                inputRef.current?.blur();
            }
            else {
                event.preventDefault();
            }
        }
    };
    /**
     * Handle value changes.
     * Remove the disabled state if the initial value differs from the input.
     * Also dynamically set size for visual fit.
     */
    const handleChange = (e) => {
        const val = e.target.value || '';
        setCurrentValue(val);
        if (e.target.parentElement) {
            e.target.parentElement.setAttribute('data-inline-editable-size', val.replaceAll(' ', '\u00a0'));
        }
        if (externalOnChange) {
            externalOnChange(e);
        }
        if (inputProps?.onChange) {
            inputProps.onChange(e);
        }
    };
    /**
     * Handle the form submit.
     * - Prevent default submit.
     * - Disable submit button.
     * - Blur input.
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        deactivate();
        // Update internal baseline if submittedValue is not used
        if (submittedValue === undefined) {
            setBaselineValue(currentValue);
        }
        setCurrentValue(currentValue);
        if (onSubmit) {
            onSubmit(e, currentValue);
        }
        if (inputRef.current) {
            inputRef.current.parentElement?.setAttribute('data-inline-editable-size', currentValue.replaceAll(' ', '\u00a0'));
        }
    };
    /**
     * Handle the form reset.
     * - Restore submittedValue (the last saved baseline).
     * - Remove active state.
     */
    const handleReset = (e) => {
        e.preventDefault();
        deactivate();
        // Always reset to last submitted value or internal fallback
        const baseline = submittedValue ?? baselineValue;
        setCurrentValue(baseline);
        if (inputRef.current) {
            inputRef.current.value = baseline;
            inputRef.current.parentElement?.setAttribute('data-inline-editable-size', baseline.replaceAll(' ', '\u00a0'));
        }
        if (onReset) {
            onReset(e, baseline);
        }
    };
    /**
     * Check if the submit button should be disabled
     */
    const isSubmitDisabled = () => {
        const baseline = submittedValue ?? baselineValue;
        if (currentValue === baseline)
            return true;
        if (valid === false)
            return true;
        return false;
    };
    /**
     * The InlineEditable class names.
     * @type {string}
     */
    const inlineEditableClassNameList = classNames('c-inline-editable', { 'is-active': isActive }, className).trim();
    /**
     * Edit Button
     * @type {JSX.Element}
     */
    const editButton = (React.createElement(Button, { link: true, simple: true, hiddenLabel: true, ariaLabel: editLabel, className: "c-inline-editable__btn", type: "button", icon: "semantic-edit", "data-tr-component": "Button", "data-tr-function": "Edit inline value", onClick: activate }));
    /**
     * Submit Button
     * @type {JSX.Element}
     */
    const submitButton = (React.createElement(Button, { hiddenLabel: true, className: "c-inline-editable__submit", type: "submit", icon: "semantic-checkmark", "data-tr-component": "Button", ariaLabel: submitLabel, "data-tr-function": "Submit inline value", disabled: isSubmitDisabled() }));
    /**
     * Reset Button
     * @type {JSX.Element}
     */
    const resetButton = (React.createElement(Button, { secondary: true, hiddenLabel: true, type: "reset", ariaLabel: resetLabel, className: "c-inline-editable__cancel", icon: "semantic-close", "data-tr-component": "Button", "data-tr-function": "Reset inline value" }));
    return (React.createElement("form", { "aria-label": ariaLabel, className: inlineEditableClassNameList, onReset: handleReset, onSubmit: handleSubmit },
        React.createElement("div", { className: "c-inline-editable__wrap" },
            React.createElement(Input, { modifierStates: "c-inline-editable__input", addonClassName: "c-inline-editable__input-addon c-input__addon--no-background", onClick: activate, onKeyDown: handleKeyDown, ...restOtherProps, size: 1, divProps: { 'data-inline-editable-size': currentValue || 1 }, onChange: handleChange, value: value === undefined ? undefined : currentValue, defaultValue: value === undefined ? currentValue : undefined, ref: inputRef, ...inputProps }),
            editButton,
            submitButton,
            resetButton),
        valid ? null : errorMessage));
};
